import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search, LayoutDashboard, Megaphone, Bell, Sparkles, Briefcase,
  Users, Palette, Globe, CreditCard, History, Settings, Bot,
  Activity, FileText, KeyRound, Rocket, GitMerge, ShieldAlert,
  BookOpen, Clock, X,
} from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * CommandPalette — global Cmd+K / Ctrl+K search palette.
 *
 * Searches across:
 *   - Static SPA pages (icon nav)
 *   - Accounts (/accounts)
 *   - Findings (/findings?q=)
 *   - Knowledge base (/knowledge/search?q=)
 *
 * Hand-rolled with React state — no `cmdk` dep needed. Keyboard
 * navigation: ArrowUp/Down to move, Enter to open, Esc to close.
 */

interface PaletteItem {
  id: string;
  label: string;
  subtitle?: string;
  section: 'pages' | 'accounts' | 'findings' | 'knowledge';
  icon: typeof LayoutDashboard;
  to: string;
}

const STATIC_PAGES: PaletteItem[] = [
  { id: 'p:/', label: 'Dashboard', section: 'pages', icon: LayoutDashboard, to: '/' },
  { id: 'p:/accounts', label: 'Konta', section: 'pages', icon: Megaphone, to: '/accounts' },
  { id: 'p:/alerts', label: 'Alerty', section: 'pages', icon: Bell, to: '/alerts' },
  { id: 'p:/insights', label: 'Centrum analiz', section: 'pages', icon: Sparkles, to: '/insights' },
  { id: 'p:/agency/overview', label: 'Przegląd agencji', section: 'pages', icon: Briefcase, to: '/agency/overview' },
  { id: 'p:/agency/team', label: 'Zespół', section: 'pages', icon: Users, to: '/agency/team' },
  { id: 'p:/agency/branding', label: 'Branding', section: 'pages', icon: Palette, to: '/agency/branding' },
  { id: 'p:/agency/domains', label: 'Domeny', section: 'pages', icon: Globe, to: '/agency/domains' },
  { id: 'p:/billing', label: 'Billing', section: 'pages', icon: CreditCard, to: '/billing' },
  { id: 'p:/audit-log', label: 'Audit log', section: 'pages', icon: History, to: '/audit-log' },
  { id: 'p:/settings', label: 'Ustawienia', section: 'pages', icon: Settings, to: '/settings' },
  { id: 'p:/copilot', label: 'AI Copilot', section: 'pages', icon: Bot, to: '/copilot' },
  { id: 'p:/activity', label: 'Aktywność', section: 'pages', icon: Activity, to: '/activity' },
  { id: 'p:/reports', label: 'Raporty', section: 'pages', icon: FileText, to: '/reports' },
  { id: 'p:/settings/security', label: 'Bezpieczeństwo', section: 'pages', icon: KeyRound, to: '/settings/security' },
  { id: 'p:/campaigns/new', label: 'Nowa kampania', section: 'pages', icon: Rocket, to: '/campaigns/new' },
  { id: 'p:/mutations', label: 'Zmiany', section: 'pages', icon: GitMerge, to: '/mutations' },
  { id: 'p:/findings', label: 'Diagnostyka', section: 'pages', icon: ShieldAlert, to: '/findings' },
  { id: 'p:/knowledge', label: 'Baza wiedzy', section: 'pages', icon: BookOpen, to: '/knowledge' },
  { id: 'p:/knowledge/admin', label: 'Admin bazy wiedzy', section: 'pages', icon: BookOpen, to: '/knowledge/admin' },
  { id: 'p:/settings/notifications', label: 'Powiadomienia', section: 'pages', icon: Bell, to: '/settings/notifications' },
  { id: 'p:/settings/sla', label: 'Polityki SLA', section: 'pages', icon: Clock, to: '/settings/sla' },
  { id: 'p:/settings/gdpr', label: 'RODO i prywatność', section: 'pages', icon: ShieldAlert, to: '/settings/gdpr' },
];

export function CommandPalette() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [remoteResults, setRemoteResults] = useState<PaletteItem[]>([]);
  const [loadingRemote, setLoadingRemote] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cmd+K / Ctrl+K global hotkey
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlight(0);
      setRemoteResults([]);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounce query
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  // Fetch remote results when query is non-empty
  useEffect(() => {
    if (!debounced || debounced.length < 2) {
      setRemoteResults([]);
      return;
    }
    let cancelled = false;
    setLoadingRemote(true);
    Promise.allSettled([
      api.get('/accounts').then((r) => r.data?.data ?? r.data ?? []),
      api.get('/findings', { params: { q: debounced, per_page: 5 } }).then((r) => r.data?.data ?? []),
      api.get('/knowledge/search', { params: { q: debounced } }).then((r) => r.data?.data ?? []),
    ])
      .then(([accountsR, findingsR, knowledgeR]) => {
        if (cancelled) return;
        const items: PaletteItem[] = [];
        const q = debounced.toLowerCase();
        const acc = accountsR.status === 'fulfilled' ? accountsR.value : [];
        if (Array.isArray(acc)) {
          acc
            .filter((a: any) => {
              const hay = `${a.name ?? ''} ${a.alias ?? ''} ${a.customer_id ?? ''}`.toLowerCase();
              return hay.includes(q);
            })
            .slice(0, 5)
            .forEach((a: any) => {
              items.push({
                id: `a:${a.id}`,
                label: a.name ?? a.alias ?? `Konto #${a.id}`,
                subtitle: a.customer_id ?? a.alias ?? '',
                section: 'accounts',
                icon: Megaphone,
                to: `/accounts/${a.id}`,
              });
            });
        }
        const findings = findingsR.status === 'fulfilled' ? findingsR.value : [];
        if (Array.isArray(findings)) {
          findings.slice(0, 5).forEach((f: any) => {
            items.push({
              id: `f:${f.id}`,
              label: f.message ?? f.rule_code ?? `Finding #${f.id}`,
              subtitle: f.rule_code ?? '',
              section: 'findings',
              icon: ShieldAlert,
              to: f.account_id ? `/accounts/${f.account_id}/diagnostics` : '/findings',
            });
          });
        }
        const knowledge = knowledgeR.status === 'fulfilled' ? knowledgeR.value : [];
        if (Array.isArray(knowledge)) {
          knowledge.slice(0, 5).forEach((k: any) => {
            items.push({
              id: `k:${k.slug}`,
              label: k.title ?? k.slug,
              subtitle: k.category ?? '',
              section: 'knowledge',
              icon: BookOpen,
              to: `/knowledge?slug=${k.slug}`,
            });
          });
        }
        setRemoteResults(items);
      })
      .finally(() => {
        if (!cancelled) setLoadingRemote(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  const filteredPages = useMemo(() => {
    if (!query.trim()) return STATIC_PAGES.slice(0, 10);
    const q = query.toLowerCase();
    return STATIC_PAGES.filter((p) => p.label.toLowerCase().includes(q)).slice(0, 8);
  }, [query]);

  const allResults = useMemo(() => [...filteredPages, ...remoteResults], [filteredPages, remoteResults]);

  // Reset highlight when results change
  useEffect(() => {
    setHighlight(0);
  }, [allResults.length]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, allResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = allResults[highlight];
      if (item) {
        navigate(item.to);
        setOpen(false);
      }
    }
  }

  if (!open) return null;

  const grouped = {
    pages: allResults.filter((r) => r.section === 'pages'),
    accounts: allResults.filter((r) => r.section === 'accounts'),
    findings: allResults.filter((r) => r.section === 'findings'),
    knowledge: allResults.filter((r) => r.section === 'knowledge'),
  };

  let runningIndex = -1;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" onClick={() => setOpen(false)}>
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-xl mx-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <Search size={16} className="text-slate-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('pol.cmdk.placeholder')}
            className="flex-1 bg-transparent outline-none text-sm dark:text-white placeholder-slate-400"
          />
          <kbd className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500">ESC</kbd>
          <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
            <X size={14} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto py-2">
          {loadingRemote && remoteResults.length === 0 && debounced && (
            <div className="px-4 py-2 text-xs text-slate-400">Szukam…</div>
          )}
          {allResults.length === 0 && (
            <div className="px-4 py-8 text-center text-xs text-slate-400">{t('pol.cmdk.no_results')}</div>
          )}
          {(['pages', 'accounts', 'findings', 'knowledge'] as const).map((section) => {
            const items = grouped[section];
            if (!items.length) return null;
            return (
              <div key={section} className="mb-2">
                <div className="px-4 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {t(`pol.cmdk.section_${section}`)}
                </div>
                {items.map((item) => {
                  runningIndex++;
                  const idx = runningIndex;
                  const Icon = item.icon;
                  const active = idx === highlight;
                  return (
                    <button
                      key={item.id}
                      onMouseEnter={() => setHighlight(idx)}
                      onClick={() => {
                        navigate(item.to);
                        setOpen(false);
                      }}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-2 text-left text-sm transition-colors',
                        active
                          ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50',
                      )}
                    >
                      <Icon size={16} className="shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate">{item.label}</div>
                        {item.subtitle && (
                          <div className="text-[10px] text-slate-400 truncate">{item.subtitle}</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
        <div className="border-t border-slate-200 dark:border-slate-800 px-4 py-2 flex items-center gap-3 text-[10px] text-slate-400">
          <span>{t('pol.cmdk.hint_navigate')}</span>
          <span>{t('pol.cmdk.hint_select')}</span>
          <span>{t('pol.cmdk.hint_close')}</span>
        </div>
      </div>
    </div>
  );
}
